import React, { useState, useRef, useEffect, useContext } from "react";
import { MDBSideNav, MDBSideNavMenu } from "mdb-react-ui-kit";
import { useLocation, useNavigate } from "react-router-dom";
import NavigationItem from "./NavigationItem";
import Logo from "./Logo";
import { useSidebar } from "../../../Context/SidebarContext";
import { useTranslation } from "../../../Context/TranslationContext";
import { HasPermission } from "../../../Utilities/CommonFunctions";
import { UserContext } from "../../../Context/UserContext";
import CollapsibleNavigationItem from "./CollapsibleNavigationItem";
import { ThemeContext } from "../../../Context/ThemeContext.js";
import { setHelmet } from '../../../Common/useHelmet';
import seo from '../../../Common/seo.json';

const BREAKPOINT_WIDTH = 1400;
const SIDENAV_MODES = {
  OVER: 'over',
  SIDE: 'side'
};

const Sidebar = () => {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);
  const { isSidebarOpen, toggleSidebar } = useSidebar();
  const { translations } = useTranslation();
  const [isPortalSectionOpen, setPortalSectionOpenStatus] = useState(true);
  const [currentNavMode, setCurrentNavMode] = useState(SIDENAV_MODES.SIDE);
  const currentWindowWidth = useRef(null);
  const location = useLocation();
  const [isSupportSectionOpen, setSupportSectionOpenStatus] = useState(true);
  const [isAdminSectionOpen, setAdminSectionOpenStatus] = useState(true);
  const [portalPageTitle, setPortalPageTitle] = useState("Welcome to PropertyBoss");
  const navigate = useNavigate();


  // Helper function to determine if a link is active
  const isLinkActive = (path) => location.pathname === path;

  const adjustNavBasedOnWindowSize = () => {
    if (window.innerWidth === currentWindowWidth.current) return;

    currentWindowWidth.current = window.innerWidth;

    if (window.innerWidth < BREAKPOINT_WIDTH) {
      setCurrentNavMode(SIDENAV_MODES.OVER);
      toggleSidebar(false);
    } else {
      setCurrentNavMode(SIDENAV_MODES.SIDE);
      toggleSidebar(true);
    }
  };
  const  seoOperations = (currentPath) => {
      let portalSeoData = seo.parents;
      const lastSegment = currentPath.substring(currentPath.lastIndexOf('/') + 1);
      for (const section in portalSeoData) {
          if (section && section == lastSegment) {
              setPortalPageTitle(portalSeoData[section].title);       
              break;
          }
      }
  }
  useEffect(() => {
    const portalTabs = ['/portal/resident', '/portal/owner', '/portal/vendor', '/portal/application', '/portal/payment'];
    if (portalTabs.includes(location.pathname)) {
      setPortalSectionOpenStatus(true);
    }
    seoOperations(location.pathname);
    adjustNavBasedOnWindowSize();
    window.addEventListener("resize", adjustNavBasedOnWindowSize);

    return () => {
      window.removeEventListener("resize", adjustNavBasedOnWindowSize);
    };
  }, [location.pathname, isSidebarOpen]);

  const moveToClick = (e, url) => {
    e.preventDefault();
    if (window.innerWidth < BREAKPOINT_WIDTH) {
      toggleSidebar(false);
    }
    navigate(url);
  }

  return (
    <>
      <MDBSideNav open={isSidebarOpen} mode={currentNavMode} backdrop={false} style={{ marginTop: '62px' }}>
        {portalPageTitle && setHelmet(portalPageTitle)}
        <MDBSideNavMenu>
          <>
          <NavigationItem click={(e) => moveToClick(e, "/portal/home")} icon="home" label="Home" link="/portal/home" isActive={isLinkActive('/portal/home')} />
          
          {HasPermission('ResidentPortal') && (
            <NavigationItem click={(e) => moveToClick(e, "/portal/resident")} icon="fas fa-house-user" label={translations.Tenant ? translations.Tenant+"s" + " " : "Tenants"} link="/portal/resident" isActive={isLinkActive('/portal/resident')} />
          )}
          {HasPermission('ProspectPortal') && (
          <NavigationItem click={(e) => moveToClick(e, "/portal/prospect")} icon="dice-d20" label="Prospects" link="/portal/prospect" isActive={isLinkActive('/portal/prospect')} />
          )}
          {HasPermission('VendorPortal') && (
            <NavigationItem click={(e) => moveToClick(e, "/portal/vendor")} icon="truck-moving" label="Vendors" link="/portal/vendor" isActive={isLinkActive('/portal/vendor')} />
          )}
          {HasPermission('OwnerPortal') && (
            <NavigationItem click={(e) => moveToClick(e, "/portal/owner")} icon="holly-berry" label={translations.owner ? translations.owner+"s" + " " : "Owners"} link="/portal/owner" isActive={isLinkActive('/portal/owner')} />
          )}
          {/* {HasPermission('PaymentPortal') && (
            <NavigationItem icon="credit-card" label="Payments" link="/portal/payment" isActive={isLinkActive('/portal/payment')} />
          )} */}
          {/* {HasPermission('ResidentPortal') && (
            <NavigationItem icon="vihara" label="Past Residents" link="/portal/pastresident" isActive={isLinkActive('/portal/pastresident')} />
          )} */}
          {HasPermission('ApplicationPortal') && (
          <CollapsibleNavigationItem
            icon="file-alt"
            label="Documents"
            isOpen={isSupportSectionOpen}
            setSectionOpenStatus={setSupportSectionOpenStatus}
            key="documents"
          >
            <NavigationItem click={(e) => moveToClick(e, "/portal/document/vault")} icon="" label="Application Vault" link="/portal/document/vault" isActive={isLinkActive('/portal/document/vault')} />
          </CollapsibleNavigationItem>
          )}
          {(user.isBetaParticipant == "true" || user.accountType == "Support")  && 0 == 1 && (
          <CollapsibleNavigationItem
            icon="cog"
            label="Administrator"
            isOpen={isAdminSectionOpen}
            setSectionOpenStatus={setAdminSectionOpenStatus}
            key="administrator"
          >
            {0 == 1 && (
              <>
                <NavigationItem click={(e) => moveToClick(e, "/administrator/messages")} icon="" label="Messages" isActive={isLinkActive('/administrator/messages')} />
              </>
            )}
            <NavigationItem click={(e) => moveToClick(e, "/administrator/users")} icon="" label="User Management" isActive={isLinkActive('/administrator/users')} />
          </CollapsibleNavigationItem>
          )}
          {
            <NavigationItem click={(e) => moveToClick(e, "/portal/support")} icon="headphones-alt" label="Support" link="/portal/support" isActive={isLinkActive('/portal/support')} />
          }
          </>
        </MDBSideNavMenu>
        {theme === "dark" || theme === "high-contrast"
          ? <Logo src={`${process.env.REACT_APP_WEB_URL}pbs-logo-dark.svg`} />
          : <Logo src={`${process.env.REACT_APP_WEB_URL}pbs-logo.svg`} />
        }
      </MDBSideNav>
    </>
  );
}

export default Sidebar;